import { useMemo } from 'react';
import {
    add as addDuration,
    eachDayOfInterval,
    endOfWeek,
    format,
    format as formatDate,
    isSameMonth,
    startOfWeek,
} from 'date-fns';
import classNames from 'classnames';

import {
    useTimelineCursorTime,
    useTimelineInterval,
    useTimelineViewMode,
} from 'app/agriculture/AgTimeline/context/TimelineContext';

import './TimelineAxisLabel.scss';
import { getIntervalPositionWithinInterval } from 'app/agriculture/AgTimeline/utils/getIntervalPositionWithinInterval';
import { useThrottledNow } from 'app/agriculture/AgTimeline/hooks/useThrottledNow';

interface TimelineAxisLabelProps {
    timestamp: Date;
}

export function TimelineAxisLabel({ timestamp }: TimelineAxisLabelProps) {
    const {
        axisLabelType,
        axisLabelTimestampFormat,
        axisSectionDuration,
        id: viewMode,
    } = useTimelineViewMode();
    const timelineInterval = useTimelineInterval();
    const cursorTime = useTimelineCursorTime();
    const now = useThrottledNow();

    const { left, right } = useMemo(() => {
        const sectionInterval = {
            start: timestamp,
            end: addDuration(timestamp, axisSectionDuration),
        };

        return getIntervalPositionWithinInterval(sectionInterval, timelineInterval);
    }, [timestamp, timelineInterval, axisSectionDuration]);

    return useMemo(
        () => (
            <div
                className={classNames('TimelineAxisLabel', `TimelineAxisLabel--${axisLabelType}`)}
                style={{
                    left: `${left * 100}%`,
                    right: `${right * 100}%`,
                }}
            >
                <div className="TimelineAxisLabel__timestamp">
                    {formatDate(timestamp, axisLabelTimestampFormat)}
                </div>
                {viewMode === 'Month' && (
                    <div className="TimelineAxisLabel__days">
                        {eachDayOfInterval({
                            start: startOfWeek(timestamp, { weekStartsOn: 1 }),
                            end: endOfWeek(timestamp, { weekStartsOn: 1 }),
                        }).map((day) => (
                            <span
                                key={format(day, 'iiiii')}
                                className={classNames('TimelineAxisLabel__day', {
                                    'TimelineAxisLabel__day--disabled': !isSameMonth(
                                        cursorTime ?? now,
                                        day,
                                    ),
                                })}
                            >
                                {formatDate(day, 'iiiii')}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        ),
        [
            timestamp,
            left,
            right,
            axisLabelTimestampFormat,
            axisLabelType,
            cursorTime,
            viewMode,
            now,
        ],
    );
}
